/**
 * IubendaPolicyEmbed (Epic 33, Story 33-2).
 *
 * Renders an Iubenda auto-generated policy (privacy or cookie) via Iubenda's
 * documented embed pattern: an `.iubenda-embed` anchor + the one-time
 * `iubenda.js` loader, which replaces the anchor with the embedded policy.
 *
 * DORMANT-UNTIL-PROVISIONED: when the Iubenda ids are not yet configured
 * (`isIubendaConfigured() === false`), this renders a fallback that points at
 * the static Story-0.7 privacy page instead — so the route is never broken
 * pre-provisioning. The loader script is injected CLIENT-SIDE in a `useEffect`
 * (never during SSR/prerender) per ADR-0005.
 */
import { useEffect } from "react";
import { Link } from "@tanstack/react-router";
import {
  isIubendaConfigured,
  iubendaPolicyUrl,
  IUBENDA_LOADER_SRC,
  type IubendaPolicyKind,
} from "../../lib/compliance/iubenda-config";

interface IubendaPolicyEmbedProps {
  kind: IubendaPolicyKind;
  /** Human label for the embed anchor / fallback heading. */
  title: string;
}

/** Inject the Iubenda loader exactly once per document. */
function ensureIubendaLoader(): void {
  if (typeof document === "undefined") return;
  if (document.querySelector(`script[src="${IUBENDA_LOADER_SRC}"]`)) return;
  const script = document.createElement("script");
  script.src = IUBENDA_LOADER_SRC;
  script.async = true;
  document.body.appendChild(script);
}

export function IubendaPolicyEmbed({ kind, title }: IubendaPolicyEmbedProps): React.ReactElement {
  const configured = isIubendaConfigured();
  const url = iubendaPolicyUrl(kind);

  useEffect(() => {
    if (!configured || !url) return;
    ensureIubendaLoader();
  }, [configured, url]);

  if (!configured || !url) {
    // Pre-provisioning fallback — never a broken page.
    return (
      <div
        className="rounded-[22px] bg-muted/50 p-6 text-base leading-relaxed shadow-xs"
        style={{ border: "0.5px solid var(--hairline)" }}
        data-testid="iubenda-fallback"
      >
        <p className="font-medium">{title}</p>
        <p className="mt-2 text-muted-foreground">
          Our {kind === "cookie" ? "cookie" : "privacy"} policy is being migrated to a
          continuously-maintained version. In the meantime, see our{" "}
          <Link to="/privacy" className="underline hover:text-foreground">
            Privacy Policy
          </Link>
          , which documents the cookies and data we use.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="iubenda-embed">
      {/* Iubenda replaces this anchor with the embedded policy once iubenda.js loads. */}
      <a
        href={url}
        className="iubenda-white no-brand iubenda-noiframe iubenda-embed"
        title={title}
      >
        {title}
      </a>
    </div>
  );
}
